import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from '@modules/translation';
import { LoadingStatus, Currency } from '@typings/common';
import { Switcher, Flex, P, Whitespace, InputNumber, Text } from '@fcc/ui';
import { formatCurrency } from '@lib/format';
import { nspkCroppedLogo } from '@constants/logos';
import { CONDITIONS_LINK } from '@constants/externalLinks';
import { useQueryParams } from '@hooks/useQueryParams';
import { Likes } from '@ui/likes';
import { useAppDispatch } from '@store/store';
import { fetchMerchantInfoAction } from '@store/merchant-info/actions';
import { fetchQrRegisterAction, clearQrRegisterAction } from '@store/qr-code/actions';
import { fetchPaymentStatusAction, clearPaymentStatusAction } from '@store/payment-status/actions';
import { merchantNameSelector, merchantInfoStatus } from '@store/merchant-info/selectors';
import { qrCodeRegisterStatus, qrRegisteredSelector } from '@store/qr-code/selectors';
import { EQrCodesType, CallbackOperations, ChargeFrequency } from '@store/qr-code/types';
import { PAYMENT_STATUS, WIDGET_TYPE } from '@src/constants/common';
import { WidgetEmptyState } from '@ui/widget-empty-state';
import { paymentStatusSelector } from '@store/payment-status/selectors';
import { getValidationSchema } from './validation-schema';
import {
    WidgetWrapper,
    WidgetBody,
    CustomButton,
    CustomP,
    NspkLogoBox,
    CustomLink,
    WidgetHeader,
    ClientLogo,
    DefaultAmountButton,
    ClickableP,
} from './styles';

export const Widget = () => {
    const { translate } = useTranslation();
    const { publicId, subscriptionPurpose, logo, widgetType, buttonColor, textButtonColor, offer, extra, width } =
        useQueryParams();
    const dispatch = useAppDispatch();

    const [isSubscription, setIsSubscription] = useState<boolean>(false);
    const [isWaitingForTransaction, setIsWaitingForTransaction] = useState<boolean>(false);

    const merchantName = useSelector(merchantNameSelector);
    const fetchMerchantInfoStatus = useSelector(merchantInfoStatus);
    const qrRegistered = useSelector(qrRegisteredSelector);
    const fetchQrRegisterStatus = useSelector(qrCodeRegisterStatus);
    const paymentStatus = useSelector(paymentStatusSelector);

    const { isLoadingApp, isFailedApp, isSuccessApp } = useMemo(
        () => ({
            isLoadingApp: fetchMerchantInfoStatus === LoadingStatus.Loading,
            isFailedApp: fetchMerchantInfoStatus === LoadingStatus.Failure,
            isSuccessApp: fetchMerchantInfoStatus === LoadingStatus.Success,
        }),
        [fetchMerchantInfoStatus]
    );

    const { isLoadingQrRegister, isFailedQrRegister } = useMemo(
        () => ({
            isLoadingQrRegister: fetchQrRegisterStatus === LoadingStatus.Loading,
            isFailedQrRegister: fetchQrRegisterStatus === LoadingStatus.Failure,
        }),
        [fetchQrRegisterStatus]
    );

    const { isPaymentRejected, isPaymentAccepted } = useMemo(
        () => ({
            isPaymentRejected: paymentStatus === PAYMENT_STATUS.RJCT,
            isPaymentAccepted: paymentStatus === PAYMENT_STATUS.ACWP,
        }),
        [paymentStatus]
    );

    const defaultAmount = [300, 500, 1000];
    const isShowForm =
        publicId && subscriptionPurpose && !isFailedApp && !isFailedQrRegister && !isWaitingForTransaction;
    const isDefaultEmptyState =
        (!isShowForm || Boolean(isSuccessApp && !merchantName)) && !isFailedQrRegister && !isWaitingForTransaction;
    const isFailedEmptyState = isFailedQrRegister || isPaymentRejected;

    const formMethods = useForm({
        defaultValues: {},
        resolver: yupResolver(getValidationSchema()),
        mode: 'onTouched',
    });

    const {
        control,
        setValue,
        trigger,
        watch,
        handleSubmit,
        formState: { errors },
    } = formMethods;

    const amountValue = watch('amount');

    useEffect(() => {
        publicId && subscriptionPurpose && dispatch(fetchMerchantInfoAction({ publicId }));
        setValue('amount', 100);
    }, []);

    useEffect(() => {
        if (qrRegistered) {
            window.open(qrRegistered.payload, '_blank');
        }
    }, [qrRegistered]);

    useEffect(() => {
        let timerId: ReturnType<typeof setInterval>;

        function startTimer() {
            if (qrRegistered && !isPaymentRejected && !isPaymentAccepted) {
                timerId = setInterval(() => {
                    setIsWaitingForTransaction(true);
                    dispatch(fetchPaymentStatusAction({ qrId: qrRegistered.qrId }));
                }, 1000);
            } else {
                setIsWaitingForTransaction(false);
            }
        }

        startTimer();
        return () => clearInterval(timerId);
    }, [qrRegistered, paymentStatus]);

    const handleClickDefaultAmount = (amount: number): void => {
        setValue('amount', amount);
        trigger('amount');
    };

    const getSubmitButtonText = (): string => {
        const text = widgetType === WIDGET_TYPE.CHARITY ? translate('donate') : translate('pay');
        if (amountValue) {
            return `${text} ${formatCurrency(amountValue, Currency.RUB, { minimumFractionDigits: 0 })}`;
        } else {
            return text;
        }
    };

    const onSubmit = (): void => {
        if (amountValue) {
            const extraValue = extra ? JSON.parse(extra) : undefined;

            const subscriptionData = {
                id: uuidv4(),
                subscriptionPurpose,
                autoCharge: {
                    frequency: ChargeFrequency.MONTHLY,
                    amount: amountValue,
                },
                extra: extraValue,
            };

            const data = {
                publicId,
                qrType: EQrCodesType.QRDYNAMIC,
                amount: amountValue,
                extra: extraValue,
                order: uuidv4(),
                origin: CallbackOperations.PAYMENT,
                subscription: isSubscription ? subscriptionData : undefined,
            };

            dispatch(fetchQrRegisterAction(data));
        } else {
            trigger('amount');
        }
    };

    const handleClearForm = (): void => {
        dispatch(clearQrRegisterAction());
        dispatch(clearPaymentStatusAction());
    };

    const handleCancelPayment = (): void => {
        handleClearForm();
        setIsWaitingForTransaction(false);
    };
    const handleCloseSuccessState = (): void => {
        handleClearForm();
        window.location.reload();
    };

    return (
        <WidgetWrapper width={width}>
            {isDefaultEmptyState && <WidgetEmptyState design="default" />}
            {isLoadingApp && <WidgetEmptyState design="loading" />}
            {isFailedEmptyState && <WidgetEmptyState design="error" action={() => handleClearForm()} />}
            {isPaymentAccepted && (
                <WidgetEmptyState
                    design="success"
                    isSubscription={isSubscription}
                    action={() => handleCloseSuccessState()}
                    widgetType={widgetType}
                    merchantName={merchantName}
                    amount={amountValue}
                />
            )}
            {isWaitingForTransaction && (
                <WidgetEmptyState
                    design="waiting"
                    action={() => handleCancelPayment()}
                    merchantName={merchantName}
                    paymentLink={qrRegistered?.payload}
                />
            )}
            {isShowForm && merchantName && !isLoadingApp && !isPaymentRejected && !isPaymentAccepted && (
                <>
                    <WidgetHeader justifyContent="space-between">
                        <Whitespace>
                            <P weight="medium">{merchantName}</P>
                            <P size="s" color="seattle100">
                                {subscriptionPurpose}
                            </P>
                        </Whitespace>
                        {logo && <ClientLogo src={logo} height={32} />}
                    </WidgetHeader>
                    <WidgetBody>
                        <Whitespace mb="s1">
                            <Controller
                                control={control}
                                name="amount"
                                render={({ field }) => (
                                    <InputNumber
                                        {...field}
                                        scale={2}
                                        placeholder={translate('enterTheAmount')}
                                        invalid={!!errors.amount}
                                        value={amountValue}
                                        onChange={(value) => field.onChange(value || undefined)}
                                        postfix={<Text color="seattle100">₽</Text>}
                                        disabled={isLoadingQrRegister}
                                    />
                                )}
                            />
                        </Whitespace>
                        <Flex gap="s1" alignItems="center" justifyContent="center" mb="s4">
                            {defaultAmount.map((amount, index) => (
                                <Flex.Item key={amount} grow="1">
                                    <DefaultAmountButton
                                        grid={12}
                                        size="xs"
                                        design="white"
                                        disabled={isLoadingQrRegister}
                                        color={buttonColor}
                                        isActive={amountValue === amount}
                                        onClick={() => handleClickDefaultAmount(amount)}>
                                        {amount}
                                        <Likes
                                            count={index + 1}
                                            isActive={amountValue === amount}
                                            color={buttonColor}
                                        />
                                    </DefaultAmountButton>
                                </Flex.Item>
                            ))}
                        </Flex>
                        <Flex justifyContent="space-between" pb="s2">
                            <ClickableP size="s" color="seattle100" onClick={() => setIsSubscription(!isSubscription)}>
                                {translate('subscriptionSwitchText')}
                            </ClickableP>
                            <Switcher
                                checked={isSubscription}
                                disabled={isLoadingQrRegister}
                                onChange={() => setIsSubscription(!isSubscription)}
                            />
                        </Flex>
                        <CustomButton
                            grid={10}
                            onClick={handleSubmit(onSubmit)}
                            loading={isLoadingQrRegister}
                            textColor={textButtonColor}
                            backgroundColor={buttonColor}>
                            {!isLoadingQrRegister && (
                                <Flex gap="s1">
                                    <NspkLogoBox>{nspkCroppedLogo}</NspkLogoBox>
                                    <P>{getSubmitButtonText()}</P>
                                </Flex>
                            )}
                        </CustomButton>
                        <CustomP color="seattle100">
                            {translate('agreementText')}
                            {!offer && (
                                <CustomLink href={CONDITIONS_LINK} target="_blank">
                                    {translate('withConditions')}
                                </CustomLink>
                            )}
                            {offer && (
                                <>
                                    <CustomLink href={CONDITIONS_LINK} target="_blank">
                                        {translate('withConditions')}
                                    </CustomLink>
                                    {translate('and')}
                                    <CustomLink href={offer} target="_blank">
                                        {translate('offer')}
                                    </CustomLink>
                                </>
                            )}
                        </CustomP>
                    </WidgetBody>
                </>
            )}
        </WidgetWrapper>
    );
};
